import { useEffect, useState } from "react";

export interface ClockProps {
  ms: number;
  /** Whether this side's time is currently being spent. */
  running: boolean;
  /** Local timestamp when `ms` was measured. */
  asOf: number;
  label: string;
}

function format(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;

  // Under ten seconds, tenths tell you whether it's worth pre-moving.
  if (ms < 10_000) {
    return `${Math.max(0, ms / 1000).toFixed(1)}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function Clock({ ms, running, asOf, label }: ClockProps) {
  const [now, setNow] = useState(() => Date.now());

  // Only tick while this clock is actually running; an idle clock re-rendering
  // ten times a second is pure waste.
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [running]);

  // Elapsed is measured entirely from local timestamps, so a browser clock that
  // disagrees with the server's makes no difference.
  const remaining = running ? Math.max(0, ms - (now - asOf)) : ms;
  const low = remaining <= 20_000;

  return (
    <div
      className={`clock${running ? " clock--running" : ""}${low ? " clock--low" : ""}`}
      role="timer"
      aria-label={`${label} clock`}
    >
      <span className="clock__time">{format(remaining)}</span>
    </div>
  );
}
